import React, { FC, useMemo } from 'react';
import { type ControllerRenderProps, useController } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  AmountInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  inputVariants,
} from '@column/ui';

import { RenderFields } from '~/components/RenderFields';

import {
  type ApprovalCondition,
  type ConfigurableApprovalRuleKey,
  approvalAmountFromMicros,
  approvalAmountToMicros,
} from './_utils/transferApproval';
import type { PlatformSettingsFields } from './index';

interface TransferTypeRow {
  label: string;
  ruleKey: ConfigurableApprovalRuleKey;
}

type ApprovalConditionFieldName = `approvalConditions.${ConfigurableApprovalRuleKey}`;

const APPROVAL_CONDITIONS: { icon: React.ReactNode; label: string; value: ApprovalCondition }[] = [
  {
    icon: <Icon.Close className="[--icon-color:var(--color-red-600)]!" />,
    value: 'disabled',
    label: 'Not required',
  },
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    value: 'all',
    label: 'Always required',
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    value: 'threshold',
    label: 'When amount is greater than',
  },
];

const ApprovalConditionFields: FC<{
  canConfigure: boolean;
  conditionField: ControllerRenderProps<PlatformSettingsFields, ApprovalConditionFieldName>;
  row: TransferTypeRow;
}> = ({ canConfigure, conditionField, row }) => {
  const amountName = `approvalAmounts.${row.ruleKey}` as const;
  const { field: amountField, fieldState: amountState } = useController<PlatformSettingsFields, typeof amountName>({
    name: amountName,
    defaultValue: undefined,
    rules: {
      validate: (value, formValues) => {
        if (formValues.approvalConditions?.[row.ruleKey] !== 'threshold') {
          return true;
        }
        return (value !== undefined && Number.isFinite(value) && value > 0) || 'Enter an amount greater than 0.';
      },
    },
  });
  const usesAmount = conditionField.value === 'threshold';
  const selectedCondition = APPROVAL_CONDITIONS.find((option) => option.value === conditionField.value);

  return (
    <div className="flex w-full flex-col items-stretch">
      <Select
        disabled={!canConfigure}
        value={conditionField.value}
        onValueChange={(value) => conditionField.onChange(value as ApprovalCondition)}
      >
        <SelectTrigger
          id={`transfer-approval-rule-condition-${row.ruleKey}`}
          aria-label={`${row.label} approval condition`}
          className="w-full"
        >
          <SelectValue>
            <span className="flex items-center gap-2">
              {selectedCondition?.icon}
              {selectedCondition?.label}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {APPROVAL_CONDITIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                {option.icon}
                {option.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div
        aria-hidden={!usesAmount}
        className={cn(
          'grid w-full transition-[grid-template-rows,transform,padding-top] ease-out',
          usesAmount
            ? 'grid-rows-[1fr] translate-y-0 pt-2 delay-0 duration-250'
            : 'grid-rows-[0fr] -translate-y-1 pt-0 delay-[325ms] duration-250'
        )}
      >
        <div className={cn('min-w-0', usesAmount ? 'overflow-visible' : 'overflow-hidden')}>
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="relative h-9 w-5 shrink-0">
              <span
                className={cn(
                  'absolute top-0 left-2 h-[18px] origin-top border-l border-gray-300 transition-transform duration-100 ease-out dark:border-gray-700',
                  usesAmount ? 'scale-y-100 delay-0' : 'scale-y-0 delay-[225ms]'
                )}
              />
              <span
                className={cn(
                  'absolute top-[18px] left-2 w-3 origin-left border-t border-gray-300 transition-transform ease-out dark:border-gray-700',
                  usesAmount ? 'scale-x-100 delay-75 duration-100' : 'scale-x-0 delay-150 duration-75'
                )}
              />
            </span>
            <div
              className={cn(
                'flex min-w-0 flex-1 origin-left items-center gap-2 transition-[clip-path,opacity,transform] ease-[cubic-bezier(0.22,1,0.36,1)]',
                usesAmount
                  ? 'opacity-100 delay-125 duration-[400ms] [clip-path:inset(-4px_-4px_-4px_0)] [transform:scaleX(1)]'
                  : 'opacity-0 delay-0 duration-200 [clip-path:inset(-4px_100%_-4px_0)] [transform:scaleX(0.96)]'
              )}
            >
              <span className="shrink-0 text-sm font-medium text-gray-600 dark:text-gray-100">
                Amount <span className="text-gray-400">≥</span>
              </span>
              <AmountInput
                id={`transfer-approval-rule-amount-${row.ruleKey}`}
                aria-label={`${row.label} approval threshold amount`}
                aria-invalid={!!amountState.error}
                className="min-w-0"
                containerClassName={cn(
                  inputVariants(),
                  'flex items-center focus-within:[--input-border-color:var(--color-blue-500)] focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] focus-within:[--input-ring-size:3px] dark:focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)]',
                  amountState.error
                    ? '[--input-border-color:var(--color-destructive)] [--input-ring-color:oklch(0.469_0.173_10.9/0.2)] [--input-ring-size:3px] dark:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]'
                    : undefined
                )}
                disabled={!canConfigure || !usesAmount}
                onBlur={amountField.onBlur}
                onValueChange={(value) => amountField.onChange(approvalAmountToMicros(value))}
                placeholder="0.00"
                prefix={<span className="mr-1 text-muted-foreground">$</span>}
                value={approvalAmountFromMicros(amountField.value)}
              />
            </div>
          </div>
          {amountState.error?.message && <span className="sr-only">{amountState.error.message}</span>}
        </div>
      </div>
    </div>
  );
};

const TRANSFER_TYPES: TransferTypeRow[] = [
  { label: 'ACH Credit', ruleKey: 'achCredits' },
  { label: 'ACH Debit', ruleKey: 'achDebits' },
  { label: 'Issue Check', ruleKey: 'checkDebits' },
  { label: 'International Wire', ruleKey: 'internationalWire' },
  { label: 'Wire Transfer', ruleKey: 'wire' },
  { label: 'Realtime', ruleKey: 'realtime' },
];

interface TransferApprovalConfigurationProps {
  canConfigure: boolean;
  enableSwift: boolean;
}

export const TransferApprovalConfiguration: FC<TransferApprovalConfigurationProps> = ({
  canConfigure,
  enableSwift,
}) => {
  const rows = useMemo(
    () => TRANSFER_TYPES.filter((row) => enableSwift || row.ruleKey !== 'internationalWire'),
    [enableSwift]
  );

  return RenderFields<PlatformSettingsFields>({
    sections: [
      {
        variant: 'isCompact',
        fields: rows.map((row) => ({
          id: `approvalConditions.${row.ruleKey}` as ApprovalConditionFieldName,
          label: row.label,
          defaultValue: 'disabled',
          children: (conditionField) => (
            <ApprovalConditionFields
              canConfigure={canConfigure}
              conditionField={
                conditionField as ControllerRenderProps<PlatformSettingsFields, ApprovalConditionFieldName>
              }
              row={row}
            />
          ),
        })),
      },
    ],
  });
};
